/**
 * cart-upsell.js
 * ---------------------------------------------------------------
 * Web component <cart-upsell> usato dallo snippet cart-upsell.liquid.
 *
 * Cosa fa:
 * 1. Al caricamento, chiede a Shopify (/recommendations/products.json)
 *    quali prodotti consigliare in base all'ultimo articolo aggiunto
 *    al carrello, e li mostra in un carosello orizzontale.
 * 2. Ogni card ha un pulsante "Aggiungi" (con selettore di variante se
 *    il prodotto ne ha più di una) che aggiunge il prodotto al
 *    carrello via AJAX e aggiorna il drawer senza ricaricare la pagina.
 *
 * Non dipende da classi interne del tema: usa solo le API pubbliche
 * di Shopify (/recommendations/products.json e /cart/add.js), quindi
 * resta valido anche se il tema viene aggiornato.
 */

if (!customElements.get('cart-upsell')) {
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('#CartUpsell-Track');
      this.productId = this.dataset.productId;
      this.limit = this.dataset.limit || 6;
      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || 'it-IT';

      if (!this.productId || !this.track) return;

      this.loadRecommendations();
    }

    async loadRecommendations() {
      this.track.innerHTML = '<div class="cart-upsell__loading" aria-hidden="true"></div>';

      try {
        const url = `/recommendations/products.json?product_id=${this.productId}&limit=${this.limit}&intent=related`;
        const response = await fetch(url);
        if (!response.ok) throw new Error('Recommendations request failed');

        const data = await response.json();
        const products = (data.products || []).filter((product) => product.available);

        if (products.length === 0) {
          this.closest('.cart-upsell, cart-upsell')?.classList.add('cart-upsell--empty');
          this.remove();
          return;
        }

        this.renderProducts(products);
      } catch (error) {
        console.warn('cart-upsell: impossibile caricare i consigli prodotto', error);
        this.remove();
      }
    }

    formatMoney(amount) {
      // L'endpoint /recommendations/products.json restituisce i prezzi in
      // centesimi (come l'Ajax Cart API), non in euro decimali: es. 839 = 8,39€.
      const cents = typeof amount === 'string' ? parseFloat(amount) : amount;
      const value = cents / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(value);
      } catch (error) {
        return `${value.toFixed(2)} ${this.currency}`;
      }
    }

    renderProducts(products) {
      this.track.innerHTML = '';

      products.forEach((product) => {
        this.track.appendChild(this.buildCard(product));
      });
    }

    buildCard(product) {
      const card = document.createElement('div');
      card.className = 'cart-upsell__card';

      const image = product.featured_image || (product.images && product.images[0]) || null;
      const hasVariants = product.variants && product.variants.length > 1;
      const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];

      const priceHtml = this.buildPriceHtml(firstAvailable);
      const variantSelectHtml = hasVariants ? this.buildVariantSelectHtml(product) : '';

      card.innerHTML = `
        <a href="${product.url}" class="cart-upsell__image-link" tabindex="-1">
          ${
            image
              ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="120" height="120" class="cart-upsell__image">`
              : ''
          }
        </a>
        <a href="${product.url}" class="cart-upsell__title">${this.escapeHtml(product.title)}</a>
        <div class="cart-upsell__price-row">${priceHtml}</div>
        ${variantSelectHtml}
        <button type="button" class="cart-upsell__add-button button button--secondary">
          <span class="cart-upsell__add-label">Aggiungi</span>
        </button>
      `;

      const select = card.querySelector('.cart-upsell__variant-select');
      const button = card.querySelector('.cart-upsell__add-button');
      const priceRow = card.querySelector('.cart-upsell__price-row');

      if (select) {
        // Il prezzo mostrato era quello della prima variante disponibile
        // e non si aggiornava mai: cambiando l'opzione nel menu (es.
        // "Titanio" -> "Inox") il testo restava fermo al prezzo iniziale.
        // Ricalcoliamo il prezzo ad ogni cambio di variante selezionata.
        select.addEventListener('change', () => {
          const selectedVariant = product.variants.find(
            (variant) => String(variant.id) === select.value
          );
          if (selectedVariant && priceRow) {
            priceRow.innerHTML = this.buildPriceHtml(selectedVariant);
          }
        });
      }

      button.addEventListener('click', () => {
        const variantId = select ? select.value : firstAvailable.id;
        this.addToCart(variantId, button);
      });

      return card;
    }

    buildPriceHtml(variant) {
      if (!variant) return '';
      const price = this.formatMoney(variant.price);
      const compareAtPrice = variant.compare_at_price;
      const onSale = compareAtPrice && parseFloat(compareAtPrice) > parseFloat(variant.price);

      if (onSale) {
        return `
          <s class="cart-upsell__compare-price">${this.formatMoney(compareAtPrice)}</s>
          <span class="cart-upsell__price cart-upsell__price--sale">${price}</span>
        `;
      }

      return `<span class="cart-upsell__price">${price}</span>`;
    }

    buildVariantSelectHtml(product) {
      const options = product.variants
        .map((variant) => {
          const disabled = variant.available ? '' : 'disabled';
          return `<option value="${variant.id}" ${disabled}>${this.escapeHtml(variant.title)}</option>`;
        })
        .join('');

      return `<select class="cart-upsell__variant-select">${options}</select>`;
    }

    async addToCart(variantId, button) {
      button.classList.add('is-loading');
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.description || 'Impossibile aggiungere il prodotto al carrello');
        }

        this.refreshCartSections(result.sections);
        this.openCartDrawer();
      } catch (error) {
        console.warn('cart-upsell: errore durante l\'aggiunta al carrello', error);
        button.classList.add('has-error');
        setTimeout(() => button.classList.remove('has-error'), 2000);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }

    refreshCartSections(sections) {
      // Importante: aggiorniamo il CONTENUTO (innerHTML) dei nodi esistenti,
      // non li sostituiamo con replaceWith(). Sostituire interi nodi mentre
      // il browser sta ancora gestendo il click che ha avviato la richiesta
      // puo' lasciare la pagina visivamente bianca finche' non si ricarica:
      // e' lo stesso approccio, piu' sicuro, usato dal tema per il resto
      // del carrello (cart.js / cart-drawer.js).
      if (!sections) return;

      if (sections['cart-drawer']) {
        const newDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newDrawer = newDoc.querySelector('#CartDrawer');
        const currentDrawer = document.getElementById('CartDrawer');
        if (newDrawer && currentDrawer) {
          currentDrawer.innerHTML = newDrawer.innerHTML;
        }

        const cartDrawerEl = document.querySelector('cart-drawer');
        const newCartDrawerEl = newDoc.querySelector('cart-drawer');
        if (cartDrawerEl && newCartDrawerEl) {
          cartDrawerEl.classList.toggle('is-empty', newCartDrawerEl.classList.contains('is-empty'));
        }
      }

      if (sections['cart-icon-bubble']) {
        // ATTENZIONE: #cart-icon-bubble non ha un proprio wrapper
        // ".shopify-section" nella pagina live (vive dentro la sezione
        // header, insieme a logo/menu/ricerca). Bisogna aggiornare SOLO
        // il contenuto dell'icona stessa: risalire con closest('.shopify-section')
        // sovrascriverebbe l'intera intestazione del sito, compresi i suoi
        // fogli di stile, lasciando la pagina visivamente rotta.
        const newDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newContent = newDoc.querySelector('.shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newContent && currentBubble) {
          currentBubble.innerHTML = newContent.innerHTML;
        }
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    openCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.classList.remove('is-empty');
        drawer.open();
      }
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value || '';
      return div.innerHTML;
    }
  }

  customElements.define('cart-upsell', CartUpsell);
}

/**
 * Fix "carrello vuoto" quando si torna indietro col gesto/pulsante del
 * browser (soprattutto su mobile). Il browser a volte ripristina la
 * pagina da una cache interna (bfcache) invece di ricaricarla: in quel
 * caso il numero di articoli nell'iconcina del carrello resta quello
 * "fotografato" nel momento in cui la pagina era stata salvata in cache,
 * anche se nel frattempo il carrello vero (sul server Shopify) è cambiato.
 * Sembra che il carrello si sia svuotato, ma è solo l'icona non aggiornata.
 *
 * Soluzione: quando la pagina torna visibile da questa cache (evento
 * "pageshow" con persisted=true), rileggiamo il carrello vero dal
 * server e aggiorniamo l'iconcina.
 */
window.addEventListener('pageshow', function (event) {
  if (!event.persisted) return;

  fetch(`${window.location.pathname}?sections=cart-icon-bubble`)
    .then((response) => response.json())
    .then((sections) => {
      if (!sections || !sections['cart-icon-bubble']) return;
      const newDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const newContent = newDoc.querySelector('.shopify-section');
      const currentBubble = document.getElementById('cart-icon-bubble');
      if (newContent && currentBubble) {
        currentBubble.innerHTML = newContent.innerHTML;
      }
    })
    .catch(() => {
      /* in caso di errore lasciamo l'icona com'era, nessun danno */
    });
});
